import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { getCompletedAgreements } from '../../api/documents';

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

interface CompletedAgreementsState {
  completedAgreements: string[];
  loading: boolean;
  error: string | null;
  lastFetched: number | null;
}

const initialState: CompletedAgreementsState = {
  completedAgreements: [],
  loading: false,
  error: null,
  lastFetched: null,
};

export const fetchCompletedAgreements = createAsyncThunk<
  string[],
  number,
  { state: { completedAgreements: CompletedAgreementsState }; rejectValue: string }
>('completedAgreements/fetchCompletedAgreements', async (company_id, { getState, rejectWithValue }) => {
  const { completedAgreements, lastFetched } = getState().completedAgreements;

  if (lastFetched && Date.now() - lastFetched < CACHE_DURATION) {
    return completedAgreements;
  }

  try {
    const completedData = await getCompletedAgreements(company_id);
    return completedData;
  } catch (err) {
    console.error('Error fetching completed agreements:', err);
    if (err instanceof Error) {
      return rejectWithValue(err.message);
    } else {
      return rejectWithValue('An unknown error occurred');
    }
  }
});

const completedAgreementsSlice = createSlice({
  name: 'completedAgreements',
  initialState,
  reducers: {
    resetCompletedAgreementsState: (state) => {
      state.completedAgreements = [];
      state.loading = false;
      state.error = null;
      state.lastFetched = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCompletedAgreements.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCompletedAgreements.fulfilled, (state, action: PayloadAction<string[]>) => {
        state.loading = false;
        state.completedAgreements = action.payload;
        state.lastFetched = Date.now();
      })
      .addCase(fetchCompletedAgreements.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.loading = false;
        state.error = action.payload || null;
        console.error('Error in state:', state.error);
      });
  },
});

export const { resetCompletedAgreementsState } = completedAgreementsSlice.actions;
export default completedAgreementsSlice.reducer;
